import { createFileRoute } from '@tanstack/react-router'
import { ResponsiveLine } from '@nivo/line'
import { ResponsiveBar } from '@nivo/bar'
import { ResponsiveFunnel } from '@nivo/funnel'
import { ResponsiveTreeMap } from '@nivo/treemap'
import { ResponsiveAreaBump } from '@nivo/bump'
import { useEffect, useState } from 'react'
import React from 'react'

type ChartComponent =
  | typeof ResponsiveLine
  | typeof ResponsiveBar
  | typeof ResponsiveFunnel
  | typeof ResponsiveTreeMap
  | typeof ResponsiveAreaBump

type ChartProps = Record<string, unknown>

export const Route = createFileRoute('/chart-render')({
  component: ChartRenderer,
})

function ChartRenderer() {
  // Get chart config from URL params
  const searchParams = new URLSearchParams(window.location.search)
  const configParam = searchParams.get('config')

  // Don't set chartReady here - set it after chart renders
  useEffect(() => {
    console.log('Chart render component mounted', { hasConfig: !!configParam })
  }, [])

  if (!configParam) {
    return (
      <div id="chart-container" style={{ padding: '20px', color: 'red' }}>
        No chart configuration provided
      </div>
    )
  }

  let config
  try {
    config = JSON.parse(decodeURIComponent(configParam))
    console.log('Parsed chart config:', { type: config.type, hasData: !!config.data })
  } catch (err) {
    console.error('Failed to parse config:', err)
    return (
      <div id="chart-container" style={{ padding: '20px', color: 'red' }}>
        Invalid chart configuration: {String(err)}
      </div>
    )
  }

  const title = config.title || ''
  const height = config.height || 400
  const width = config.width || 800

  const defaultMargin = { top: 50, right: 140, bottom: 80, left: 90 }
  const margin = config.margin || defaultMargin

  let ChartComponent: ChartComponent | null = null
  let chartProps: ChartProps = {}

  // Validate config has required data
  if (!config.data) {
    return (
      <div id="chart-container" style={{ padding: '20px', color: 'red' }}>
        <strong>Malformed Chart Config:</strong> Missing 'data' property
        <br />
        <small>Config: {JSON.stringify(config, null, 2).substring(0, 500)}</small>
      </div>
    )
  }

  try {
    console.log('Setting up chart component:', { type: config.type, dataLength: Array.isArray(config.data) ? config.data.length : 'not-array' })

    switch (config.type) {
      case 'line':
        ChartComponent = ResponsiveLine
      chartProps = {
        data: config.data,
        margin: margin,
        xScale: config.xScale || { type: 'point' },
        yScale: config.yScale || { type: 'linear', min: 'auto', max: 'auto' },
        curve: config.curve || 'monotoneX',
        axisBottom: {
          tickSize: 5,
          tickPadding: 5,
          tickRotation: -45,
        },
        axisLeft: {
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
        },
        colors: config.colors || ['#8884d8', '#82ca9d', '#ff7300'],
        lineWidth: config.lineWidth || 2,
        pointSize: config.pointSize || 5,
        pointColor: { theme: 'background' },
        pointBorderWidth: 2,
        pointBorderColor: { from: 'serieColor' },
        useMesh: config.useMesh !== false,
        legends: [
          {
            anchor: 'bottom-right',
            direction: 'column',
            translateX: 100,
            itemWidth: 80,
            itemHeight: 20,
            symbolSize: 12,
          },
        ],
      }
      break

    case 'bar':
      ChartComponent = ResponsiveBar
      chartProps = {
        data: config.data,
        keys: config.keys,
        indexBy: config.indexBy,
        margin: margin,
        padding: 0.3,
        colors: config.colors || ['#8884d8', '#82ca9d', '#ff7300'],
        indexAxis: config.layout === 'horizontal' ? 'y' : 'x',
        legends: [
          {
            dataFrom: 'keys',
            anchor: 'bottom-right',
            direction: 'column',
            translateX: 120,
            itemWidth: 100,
            itemHeight: 20,
          },
        ],
      }
      break

    case 'funnel':
      ChartComponent = ResponsiveFunnel
      chartProps = {
        data: config.data,
        margin: margin,
        direction: config.direction || 'horizontal',
        colors: config.colors || ['#ef4444', '#f97316', '#eab308'],
      }
      break

    case 'treemap':
      ChartComponent = ResponsiveTreeMap
      chartProps = {
        data: config.data,
        margin: margin,
        identity: 'name',
        value: 'value',
        tile: config.tile || 'squarify',
        leavesOnly: config.leavesOnly !== false,
        colors: config.colors || { scheme: 'nivo' },
        borderWidth: 2,
      }
      break

    case 'areabump':
      ChartComponent = ResponsiveAreaBump
      chartProps = {
        data: config.data,
        margin: margin,
        spacing: 8,
        colors: config.colors || ['#8884d8', '#82ca9d', '#ff7300'],
        fillOpacity: config.fillOpacity || 0.6,
      }
      break

    default:
      return (
        <div id="chart-container" style={{ padding: '20px', color: 'red' }}>
          Unsupported chart type: {config.type}
        </div>
      )
    }
  } catch (err) {
    return (
      <div id="chart-container" style={{ padding: '20px', color: 'red' }}>
        Error setting up chart: {String(err)}
      </div>
    )
  }

  if (!ChartComponent) {
    return (
      <div id="chart-container" style={{ padding: '20px', color: 'red' }}>
        Failed to initialize chart component
      </div>
    )
  }

  // Type assertion to help TypeScript understand ChartComponent is not null
  const Component = ChartComponent as Exclude<typeof ChartComponent, null>;

  return (
    <div
      id="chart-container"
      style={{
        width: `${width}px`,
        height: `${height + 60}px`,
        background: 'white',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        padding: '16px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
    >
      {title && (
        <h3
          style={{
            textAlign: 'center',
            fontSize: '18px',
            fontWeight: 600,
            color: '#111827',
            margin: '0 0 16px 0',
          }}
        >
          {title}
        </h3>
      )}
      <div id="chart" style={{ height: `${height}px`, width: '100%' }}>
        <ErrorBoundary configType={config.type}>
          {React.createElement(Component as any, chartProps)}
        </ErrorBoundary>
      </div>
    </div>
  )
}

// Error boundary to catch chart rendering errors
function ErrorBoundary({ children, configType }: { children: React.ReactNode; configType: string }) {
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    const errorHandler = (event: ErrorEvent) => {
      console.error('Chart render error caught:', event.error)
      setHasError(true)
      setErrorMessage(event.error?.message || String(event.error))
    }

    window.addEventListener('error', errorHandler)
    return () => window.removeEventListener('error', errorHandler)
  }, [])

  if (hasError) {
    return (
      <div style={{ padding: '20px', color: 'red', border: '2px solid red' }}>
        <strong>Chart Rendering Error ({configType}):</strong>
        <br />
        {errorMessage}
      </div>
    )
  }

  try {
    return <>{children}</>
  } catch (err) {
    return (
      <div style={{ padding: '20px', color: 'red', border: '2px solid red' }}>
        <strong>Chart Rendering Error ({configType}):</strong>
        <br />
        {String(err)}
      </div>
    )
  }
}
